import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, exhaustMap, from, map, mergeMap, Observable, of, switchMap } from 'rxjs';

import { MFAPreference } from '@ninety/platform/models/auth/mfaPreference.type';
import { MfaType } from '@ninety/platform/models/auth/mfaType.enum';
import { PersonService } from '@ninety/platform/services/services/person.service';

import { LoggingActions } from '../../../app-logging/app-logging.actions';
import { appActions } from '../../../app.actions';
import { BaseEffects } from '../../../base.effects';
import {
  COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG,
  TOTP_CHANGE_AUTH_APP_DIALOG,
  TOTP_QR_CODE_DIALOG,
  TOTP_REMOVE_AUTHENTICATOR_DIALOG,
  VALIDATE_CREDENTIALS_DIALOG,
  VERIFY_PASSWORD_DIALOG,
} from '../../../tokens/auth-dialog.tokens';
import { CompanyService } from '../../company/company.service';
import { NotificationActions } from '../../notifications/notification.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { AuthActions, TotpActions } from '../auth.actions';
import { TotpQrCodeDialogInput, TotpQrCodeDialogSteps } from '../dialogs/auth-dialog.contracts';
import { LoginActions } from '../login/login.actions';
import { LogOutActions } from '../logout/logout.actions';
import { AmplifyService } from '../services/amplify.service';
import { MfaTotpVerificationFlow } from '../services/auth.types';

const apiV4 = 'api/v4';

@Injectable()
export class TotpEffects extends BaseEffects {
  action$ = inject(Actions);
  cognitoService = inject(AmplifyService);
  companyService = inject(CompanyService);
  personService = inject(PersonService);
  store = inject(Store);
  dialog = inject(MatDialog);
  http = inject(HttpClient);
  router = inject(Router);
  private readonly openScanQrCode = inject(TOTP_QR_CODE_DIALOG);
  private readonly openChangeAuthApp = inject(TOTP_CHANGE_AUTH_APP_DIALOG);
  private readonly openVerifyPassword = inject(VERIFY_PASSWORD_DIALOG);
  private readonly openRemoveAuthenticator = inject(TOTP_REMOVE_AUTHENTICATOR_DIALOG);
  private readonly openCompanyConfigureAuthenticator = inject(COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG);
  private readonly openValidateCredentials = inject(VALIDATE_CREDENTIALS_DIALOG);
  MfaTotpVerificationFlow = MfaTotpVerificationFlow;

  userSettingsOpenTotpDialog$ = this.exhaustMapEffect({
    action: TotpActions.userSettingsOpenTotpDialog,
    exhaustMapFn: ({ TotpQrCodeDialogInput: scanQrCodeDialogInput }) =>
      this.openScanQrCodeDialog({
        workflow: scanQrCodeDialogInput.workflow,
        currentStep: TotpQrCodeDialogSteps.ScanAndEnterQrCode,
        reset: false,
      }),
  });

  userSettingsOpenVerifyPasswordDialog$ = this.exhaustMapEffect({
    action: TotpActions.userSettingsOpenVerifyPasswordDialog,
    exhaustMapFn: ({ invalidPassword }) => this.openVerifyPasswordDialog(invalidPassword),
  });

  userSettingsOpenChangeQrCodeDialog$ = this.exhaustMapEffect({
    action: TotpActions.userSettingsChangeQRCode,
    exhaustMapFn: () => this.openChangeQrCodeDialog(),
  });

  userSettingsOpenDeleteAuthenticatorAppDialog$ = this.exhaustMapEffect({
    action: TotpActions.userSettingsOpenDeleteAuthenticatorAppDialog,
    exhaustMapFn: () => this.openRemoveAuthenticatorAppDialog(),
  });

  userSettingsPasswordVerified$ = this.exhaustMapEffect({
    action: TotpActions.userSettingsPasswordVerified,
    exhaustMapFn: () => this.openChangeQrCodeDialog(),
  });

  openCompanyConfigureAuthenticatorDialog$ = this.exhaustMapEffect({
    action: TotpActions.openCompanyConfigureAuthenticatorDialog,
    exhaustMapFn: ({ requireAuthenticator }) => this.openCompanyConfigureAuthenticatorDialog(requireAuthenticator),
  });

  openValidateCredentialsDialog$ = this.exhaustMapEffect({
    action: TotpActions.openValidateCredentialsDialog,
    exhaustMapFn: ({ mfaType: MfaType }) => this.openValidateCredentialsDialog(MfaType),
  });

  loginTotpMfaChallenge$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.loginDisplayMFAChallenge),
      exhaustMap(({ mfaType }) =>
        from(this.router.navigate(['login/totp'], { queryParams: { mfaType } })).pipe(map(() => appActions.noop()))
      )
    )
  );

  confirmCodeByMfaType$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.confirmCodeByMfaType),
      switchMap(({ password, email, mfaType }) => {
        return this.personService.updateUsersPreferredMfa(email, password, mfaType).pipe(
          switchMap(() => {
            return of(TotpActions.confirmCodeByMfaTypeSuccess());
          }),
          catchError((error: unknown) => {
            return of(TotpActions.confirmCodeByMfaTypeFailure({ error }));
          })
        );
      })
    )
  );

  confirmCodeByMfaTypeSuccess_logUserOut$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.confirmCodeByMfaTypeSuccess),
      map(() => {
        return LogOutActions.initiateLogOut();
      })
    )
  );

  confirmCodeByMfaTypeSuccess_notify$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.confirmCodeByMfaTypeSuccess),
      map(() => {
        return NotificationActions.notify({
          message: 'Your preferred MFA method has been updated. Please log in again using the new method.',
        });
      })
    )
  );

  confirmCodeByMfaTypeFailure$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.confirmCodeByMfaTypeFailure),
      map(({ error }) => {
        this.dialog.closeAll();
        return NotificationActions.notifyError({
          error,
          message: 'Failed to send confirmation code. Please try again.',
        });
      })
    )
  );

  submitTotpCode$ = this.switchMapEffect({
    action: TotpActions.submitTotpCode,
    switchMapFn: ({ code, workflow }) => {
      if (workflow === MfaTotpVerificationFlow.LOGIN_TOTP_SETUP) {
        return this.handleLoginTotpSetup(code);
      } else if (workflow === MfaTotpVerificationFlow.SETTINGS) {
        return this.handleSettingsTotp(code);
      } else if (workflow === MfaTotpVerificationFlow.VERIFY_TOTP_SETUP) {
        return this.handleVerifyTotpSetup(code);
      } else if (workflow === MfaTotpVerificationFlow.LOGIN_VERIFY_CODE) {
        return this.handleLoginVerifyCode(code);
      } else {
        return of(appActions.noop());
      }
    },
  });

  private handleLoginTotpSetup(code: string): Observable<Action> {
    return this.cognitoService.verifyTOTP(code).pipe(
      map(() => {
        return TotpActions.loginSetupTotpSuccess();
      }),
      catchError((error: unknown) => {
        return of(TotpActions.loginSetupTotpFailure({ error }));
      })
    );
  }

  private handleSettingsTotp(code: string): Observable<Action> {
    return this.cognitoService.confirmSignIn(code).pipe(
      map(() => {
        return TotpActions.userSettingsTotpCodeSuccess();
      }),
      catchError((error: unknown) => {
        return of(TotpActions.userSettingsTotpCodeFailure({ error }));
      })
    );
  }

  private handleVerifyTotpSetup(code: string): Observable<Action> {
    return this.cognitoService.verifyTOTP(code).pipe(
      map(() => {
        return TotpActions.userSettingsTotpCodeSuccess();
      }),
      catchError((error: unknown) => {
        return of(TotpActions.userSettingsTotpCodeFailure({ error }));
      })
    );
  }

  private handleLoginVerifyCode(code: string): Observable<Action> {
    return this.cognitoService.confirmSignIn(code).pipe(
      map(signInOutput => LoginActions.logInNextStep({ signInOutput })),
      catchError((error: unknown) => {
        const errorName = (error as any)?.name ?? '';

        // Session is gone — user must restart the login flow entirely
        if (errorName.includes('UserUnAuthenticatedException') || errorName === 'NotAuthorizedException') {
          this.store.dispatch(
            LoggingActions.warn({
              log: { message: `[TOTP] Login verify code failed — session expired (${errorName})`, error },
            })
          );
          return of(
            TotpActions.redirectToLogin({ userErrorMessage: 'Your session has expired. Please log in again.' })
          );
        }

        // CodeMismatchException, ExpiredCodeException, and any other retriable
        // errors are surfaced via loginTotpCodeFailure, which navigates back to
        // /login/totp so the user can try again
        this.store.dispatch(
          LoggingActions.warn({ log: { message: `[TOTP] Login verify code failed (${errorName})`, error } })
        );
        return of(TotpActions.loginTotpCodeFailure({ error }));
      })
    );
  }

  redirectToLogin_displayNotification$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.redirectToLogin),
      map(({ userErrorMessage }) => {
        return NotificationActions.notifyError({
          error: userErrorMessage ? new Error(userErrorMessage) : new Error('The user session has expired.'),
          message: userErrorMessage ?? 'Your session has expired. Please log in again.',
        });
      })
    )
  );

  redirectToLogin$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.redirectToLogin),
      map(({ logoutRedirectUrl }) =>
        logoutRedirectUrl
          ? LogOutActions.initiateLogOutWithSuppliedRedirect({ logoutRedirectUrl })
          : LogOutActions.initiateLogOut()
      )
    )
  );

  userSettingsTotpCodeSuccess_notify$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsTotpCodeSuccess),
      map(() => {
        this.dialog.closeAll();
        return NotificationActions.notify({
          message: 'TOTP code verified successfully.',
        });
      })
    )
  );

  // TODO: validate that we need this to refresh the child component after TOTP updates
  userSettingsTotpCodeSuccess_redirect$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsTotpCodeSuccess),
      exhaustMap(() => from(this.router.navigate(['/settings/user/account'])).pipe(map(() => appActions.noop())))
    )
  );

  userSettingsTotpCodeFailure$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsTotpCodeFailure),
      map(({ error: err }) => {
        this.dialog.closeAll();
        if ((err as any)?.name === 'SignInException') {
          this.cognitoService.autoSignIn().subscribe(() => {
            this.openScanQrCodeDialog({
              currentStep: TotpQrCodeDialogSteps.TotpCodeError,
              reset: true,
              workflow: MfaTotpVerificationFlow.SETTINGS,
            } as TotpQrCodeDialogInput);
          });
        }
        return NotificationActions.showSnackbar({
          message: 'TOTP code verification failed.',
        });
      })
    )
  );

  updateCompanyTotpAuthenticatorAppSetting$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.updateCompanyTotpAuthenticatorAppSetting),
      exhaustMap(({ requireAuthenticator }) => {
        return from(this.companyService.updateSettingsWithKeyValue('requireMfa', requireAuthenticator)).pipe(
          map(() => {
            return TotpActions.updateCompanyTotpAuthenticatorAppSettingSuccess({ requireAuthenticator });
          }),
          catchError((error: unknown) => {
            return of(TotpActions.updateCompanyTotpAuthenticatorAppSettingFailure({ error }));
          })
        );
      })
    )
  );

  updateCompanyTotpAuthenticatorAppSettingSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.updateCompanyTotpAuthenticatorAppSettingSuccess),
      map(({ requireAuthenticator }) => {
        this.dialog.closeAll();
        const message = requireAuthenticator
          ? 'MFA has been required for all users in your company.'
          : 'MFA is no longer required for all users in your company.';
        return NotificationActions.notify({
          message: message,
        });
      })
    )
  );

  updateCompanyTotpAuthenticatorAppSettingFailure$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.updateCompanyTotpAuthenticatorAppSettingFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          error,
          message: 'Failed to update company TOTP setting.',
        })
      )
    )
  );

  userSettingsSubmitVerifyPassword$ = this.switchMapEffect({
    action: TotpActions.userSettingsSubmitVerifyPassword,
    switchMapFn: ({ password }) =>
      this.http.post(`${apiV4}/Cognito/VerifyPassword`, { password }).pipe(
        map(() => TotpActions.userSettingsPasswordVerified()),
        catchError((error: unknown) => of(TotpActions.userSettingsPasswordRejected({ error })))
      ),
  });

  userSettingsDeleteTotpAuthenticatorApp$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsDeleteTotpAuthenticatorApp),
      exhaustMap(() =>
        this.cognitoService
          .deleteTOTPAuthenticatorApp()
          .then(() => TotpActions.userSettingsDeleteTotpAuthenticatorAppSuccess())
          .catch((error: unknown) => TotpActions.userSettingsDeleteTotpAuthenticatorAppFailure({ error }))
      )
    )
  );

  userSettingsDeleteTotpAuthenticatorAppSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsDeleteTotpAuthenticatorAppSuccess),
      map(() => {
        return NotificationActions.notify({
          message: 'TOTP Authenticator App removed.',
        });
      })
    )
  );

  userSettingsDeleteTotpAuthenticatorAppSuccess_attemptToUpdatePreferredMfa$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsDeleteTotpAuthenticatorAppSuccess),
      mergeMap(() =>
        this.cognitoService.fetchMfaPreference().pipe(
          mergeMap((output: { enabled: MfaType[] }) => {
            const mfaTypes = output?.enabled ?? [];
            if (mfaTypes.includes(MfaType.SMS)) {
              return of(TotpActions.updatePreferredMfa({ mfaType: MfaType.SMS }));
            } else if (mfaTypes.includes(MfaType.EMAIL)) {
              return of(TotpActions.updatePreferredMfa({ mfaType: MfaType.EMAIL }));
            } else {
              return of(appActions.noop());
            }
          })
        )
      )
    )
  );

  updateMfaPreference$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.updatePreferredMfa),
      switchMap(({ mfaType }) =>
        this.cognitoService.updateMfa(mfaType, MFAPreference.PREFERRED).pipe(
          map(() => TotpActions.updatePreferredMfaSuccess()),
          catchError((error: unknown) => of(TotpActions.updatePreferredMfaFailure({ error })))
        )
      )
    )
  );

  userSettingsDeleteTotpAuthenticatorAppFailure$ = createEffect(() =>
    this.action$.pipe(
      ofType(TotpActions.userSettingsDeleteTotpAuthenticatorAppFailure),
      map(() =>
        NotificationActions.notify({
          message: 'Failed to delete TOTP Authenticator App.',
        })
      )
    )
  );

  refreshCognitoMfa = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TotpActions.submitTotpCode,
        TotpActions.userSettingsSubmitTotpCode,
        TotpActions.loginSubmitTotpCode,
        TotpActions.userSettingsDeleteTotpAuthenticatorAppSuccess,
        TotpActions.loginSetupTotpSuccess,
        TotpActions.userSettingsTotpCodeSuccess,
        TotpActions.updatePreferredMfaSuccess
      ),
      map(() => AuthActions.getCognitoUserAttributes())
    )
  );

  showSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TotpActions.submitTotpCode,
        TotpActions.userSettingsSubmitVerifyPassword,
        TotpActions.loginSubmitTotpCode,
        TotpActions.userSettingsSubmitTotpCode
      ),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TotpActions.userSettingsPasswordVerified,
        TotpActions.userSettingsTotpCodeSuccess,
        TotpActions.userSettingsTotpCodeFailure,
        TotpActions.loginDisplayMFAChallenge,
        TotpActions.loginTotpCodeSuccess,
        TotpActions.loginTotpCodeFailure
      ),
      map(({ type }) => SpinnerActions.stopPrimary({ source: type }))
    )
  );

  private openScanQrCodeDialog(input: TotpQrCodeDialogInput): Observable<Action> {
    this.dialog.closeAll();
    return this.openScanQrCode({
      width: '720px',
      data: input,
    })
      .afterClosed()
      .pipe(
        map(result => {
          if (!result?.code) return appActions.noop();
          return TotpActions.submitTotpCode({
            code: result?.code,
            workflow: result?.workflow,
          });
        })
      );
  }

  private openChangeQrCodeDialog(): Observable<Action> {
    return this.openChangeAuthApp({
      width: '720px',
      data: {},
    })
      .afterClosed()
      .pipe(
        map(result => {
          const cancelled = result?.cancelled ?? true;
          if (cancelled) return TotpActions.userSettingsChangeQRCodeCancelled();
          return TotpActions.userSettingsChangeQRCodeSuccess();
        })
      );
  }

  private openVerifyPasswordDialog(invalidPassword: boolean): Observable<Action> {
    return this.openVerifyPassword({ data: { invalidPassword } })
      .afterClosed()
      .pipe(
        map(result => {
          const cancelled = result?.cancelled ?? true;
          const password = result?.password ?? '';
          if (cancelled) return TotpActions.userSettingsCancelVerifyPassword();
          return TotpActions.userSettingsSubmitVerifyPassword({ password });
        })
      );
  }

  private openRemoveAuthenticatorAppDialog(): Observable<Action> {
    return this.openRemoveAuthenticator({
      width: '720px',
      data: {},
    })
      .afterClosed()
      .pipe(
        map(result => {
          const cancelled = result?.cancelled ?? true;
          if (cancelled)
            return TotpActions.userSettingsDeleteTotpAuthenticatorAppFailure({ error: 'User cancelled deletion' });
          return TotpActions.userSettingsDeleteTotpAuthenticatorApp();
        })
      );
  }

  private openCompanyConfigureAuthenticatorDialog(requireAuthenticator: boolean): Observable<Action> {
    return this.openCompanyConfigureAuthenticator({
      width: '560px',
      data: { requireAuthenticator },
    })
      .afterClosed()
      .pipe(
        map(result => {
          const { cancelled } = result ?? {};
          const { requireAuthenticator } = result ?? {};
          if (!cancelled) {
            if (requireAuthenticator === undefined) {
              return TotpActions.updateCompanyTotpAuthenticatorAppSettingFailure({ error: 'User cancelled update' });
            } else {
              return TotpActions.updateCompanyTotpAuthenticatorAppSetting({ requireAuthenticator });
            }
          } else {
            return TotpActions.cancelUpdateCompanyTotpAuthenticatorAppSetting();
          }
        })
      );
  }

  private openValidateCredentialsDialog(mfaType: MfaType): Observable<Action> {
    return this.openValidateCredentials({
      data: { mfaType },
    })
      .afterClosed()
      .pipe(
        map(result => {
          const cancelled = result?.cancelled ?? true;
          const password = result?.password ?? '';
          const email = result?.email ?? '';
          const selectedMfaType = result?.mfaType ?? mfaType;
          if (cancelled) return TotpActions.cancelValidateCredentials();
          localStorage.setItem('shouldResetPreferredMfa', 'true');
          return TotpActions.confirmCodeByMfaType({ password, email, mfaType: selectedMfaType });
        })
      );
  }

  loginTotpCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginTotpCodeSuccess),
      map(() => LoginActions.redirectToApp({ response: null }))
    )
  );

  loginTotpCodeSuccess_notify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginTotpCodeSuccess),
      map(() => NotificationActions.notify({ message: 'MFA code verification successful.' }))
    )
  );

  loginTotpCodeFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginTotpCodeFailure),
      exhaustMap(() =>
        from(this.router.navigate(['/login/totp'])).pipe(
          map(() => NotificationActions.notify({ message: 'MFA code verification failed.' }))
        )
      )
    )
  );

  loginSetupTotp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginSetupTotp),
      map(() => {
        this.router.navigate(['setup-totp']);
        return appActions.noop();
      })
    )
  );

  loginSetupTotpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginSetupTotpSuccess),
      map(() => {
        this.dialog.closeAll();
        NotificationActions.notify({ message: 'TOTP setup successful.' });
        return LoginActions.redirectToApp({ response: null });
      })
    )
  );

  loginSetupTotpFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TotpActions.loginSetupTotpFailure),
      map(() => NotificationActions.notify({ message: 'TOTP setup failed. Please try again.' }))
    )
  );
}
